import { Evaluation, Unit } from './AST/types'
import { convertUnit, formatUnit, serializeUnit, simplifyUnit } from './units'

export const numberFormatter =
	({
		style,
		maximumFractionDigits = 2,
		minimumFractionDigits = 0,
		language,
	}: {
		style?: 'currency' | 'percent' | 'decimal'
		maximumFractionDigits?: number
		minimumFractionDigits?: number
		language?: string
	}) =>
	(value: number) => {
		// When we format currency we don't want to display a single decimal digit
		// ie 8,1€ but we want to display 8,10€
		const adaptedMinimumFractionDigits =
			(
				style === 'currency' &&
				maximumFractionDigits >= 2 &&
				minimumFractionDigits === 0 &&
				!Number.isInteger(value)
			) ?
				2
			:	minimumFractionDigits
		return Intl.NumberFormat(language, {
			style,
			currency: 'EUR',
			maximumFractionDigits,
			minimumFractionDigits: adaptedMinimumFractionDigits,
		}).format(value)
	}

export const formatCurrency = (
	nodeValue: number | undefined,
	language: string,
) => {
	return nodeValue == undefined ? '' : (
			(formatNumber({ unit: '€', language, nodeValue }) ?? '').replace(
				/^(-)?€/,
				'$1€\u00A0',
			)
		)
}

export const formatPercentage = (nodeValue: number | undefined) =>
	nodeValue == undefined ? '' : (
		formatNumber({ unit: '%', nodeValue, maximumFractionDigits: 2 })
	)

type formatValueOptions = {
	maximumFractionDigits?: number
	minimumFractionDigits?: number
	language?: string
	unit?: Unit | string
	formatUnit?: formatUnit
	nodeValue: number
}

function formatNumber({
	maximumFractionDigits,
	minimumFractionDigits,
	language,
	formatUnit,
	unit,
	nodeValue,
}: formatValueOptions) {
	if (typeof nodeValue !== 'number') {
		return nodeValue
	}
	const serializedUnit =
		unit ? serializeUnit(unit, nodeValue, formatUnit) : undefined
	switch (serializedUnit) {
		case '€':
			return numberFormatter({
				style: 'currency',
				maximumFractionDigits,
				minimumFractionDigits,
				language,
			})(nodeValue)
		case '%':
			return numberFormatter({
				style: 'percent',
				maximumFractionDigits,
				language,
			})(nodeValue / 100)
		default:
			return (
				numberFormatter({
					style: 'decimal',
					minimumFractionDigits,
					maximumFractionDigits,
					language,
				})(nodeValue) +
				(typeof serializedUnit === 'string' ? `\u00A0${serializedUnit}` : '')
			)
	}
}

export function capitalise0(name: undefined): undefined
export function capitalise0(name: string): string
/**
 * @internal
 */
export function capitalise0(name?: string) {
	return name && name[0].toUpperCase() + name.slice(1)
}

const booleanTranslations = {
	fr: { true: 'oui', false: 'non' },
	en: { true: 'yes', false: 'no' },
}

/**
 * Format the result of an evaluation
 *
 * Useful to display the result of a calculation in a user interface.
 * It use the {@link Unit} to format the number and the unit.
 *
 * @param value - The value to format, usually a {@link EvaluatedNode}
 * @param options - Options to customize the formatting
 * @param options.language - @internal The language in which to display the result
 * @param options.displayedUnit - The unit to display, if different from the unit of the value
 * @param options.precision - The number of decimal digits to display
 *
 * @example
 *
 * ```ts
 * formatValue(engine.evaluate('3 € + 2 €')) // '5 €'
 * ```
 */
export function formatValue(
	value: number | { nodeValue: Evaluation; unit?: Unit } | undefined,
	{
		language = 'fr',
		displayedUnit,
		formatUnit,
		precision = 2,
	}: {
		language?: string
		displayedUnit?: string
		precision?: number
		formatUnit?: formatUnit
	} = {},
) {
	let nodeValue =
		(
			typeof value === 'number' ||
			typeof value === 'undefined' ||
			value === null
		) ?
			value
		:	value.nodeValue

	if (typeof nodeValue === 'number' && Number.isNaN(nodeValue)) {
		return 'Erreur dans le calcul du nombre'
	}
	if (nodeValue === undefined) {
		return 'Pas encore défini'
	}
	if (nodeValue === null) {
		return 'Non applicable'
	}
	if (typeof nodeValue === 'string') {
		return nodeValue.replace('\\n', '\n')
	}
	if (typeof nodeValue === 'boolean')
		return booleanTranslations[language][nodeValue]
	if (typeof nodeValue === 'number') {
		let unit =
			(
				typeof value === 'number' ||
				typeof value === 'undefined' ||
				!('unit' in value)
			) ?
				undefined
			:	value.unit
		if (unit) {
			const simplifiedUnit = simplifyUnit(unit)
			nodeValue = convertUnit(unit, simplifiedUnit, nodeValue)
			unit = simplifiedUnit
		}
		return formatNumber({
			minimumFractionDigits: 0,
			maximumFractionDigits: precision,
			language,
			formatUnit,
			nodeValue,
			unit: displayedUnit ?? unit,
		}).trim()
	}
	return undefined
}

export function serializeValue(
	{ nodeValue, unit }: { nodeValue: Evaluation; unit?: Unit },
	{ format }: { format: formatUnit },
) {
	const serializedUnit = (
		unit && typeof nodeValue === 'number' ?
			serializeUnit(unit, nodeValue, format)
		:	'')?.replace(/\s*\/\s*/g, '/')
	return `${nodeValue} ${serializedUnit}`.trim()
}
